/**
 * Martianblue Cyber Defense — Cyber Education Admin Panel
 * Route: /cyber-edu-admin
 *
 * Features:
 *  - Login with math-CAPTCHA (email + password)
 *  - Event management (webinars, summer schools, etc.)
 *  - Per-event registrant list with drawer details
 *  - Global search (email / phone / college) across all events
 *  - Filter: All / specific event
 *  - Email campaign: single or bulk, from communication@martianbluecyberdefense.in
 *  - Create new event (name, type, date)
 *
 * Backend endpoints expected on your VPS (http://localhost:5000):
 *   POST   /api/admin/login               → { token, user? }
 *   GET    /api/admin/events              → [{_id, name, type, date, count}]
 *   POST   /api/admin/events              → created event object
 *   GET    /api/admin/registrations?eventId=…&search=…  → array of registrant objects
 *   POST   /api/admin/send-campaign       → { to:[emails], subject, body, from }
 *
 * The existing webinarregistrations collection in martian-blue db
 * is automatically picked up when eventId is omitted (returns all).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/* ─── CONFIG ─────────────────────────────────────────────── */
const API_BASE = process.env.NODE_ENV === 'production' 
  ? 'https://martianbluecyberdefense.in' 
  : 'http://localhost:5000';
const FROM_EMAIL = 'cyberedu@martianbluecyberdefense.in';

/* ─── API HELPER ─────────────────────────────────────────── */
const api = async (path, opts = {}) => {
  const token = sessionStorage.getItem('mbcd_admin_token');
  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...opts,
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || res.statusText);
  return res.json();
};

/* ─── DESIGN TOKENS ──────────────────────────────────────── */
const T = {
  bg:       '#0a2540',
  surface:  '#0d3a5c',
  card:     '#0d2847',
  border:   'rgba(0,212,255,0.18)',
  borderHi: 'rgba(0,212,255,0.6)',
  cyan:     '#00d4ff',
  cyanDim:  '#00b4d8',
  text:     '#e2e8f0',
  muted:    '#7a8fa8',
  success:  '#22c55e',
  error:    '#ef4444',
  font:     "'Poppins', sans-serif",
  display:  "'Rajdhani', sans-serif",
};

const baseInp = {
  display: 'block', width: '100%', boxSizing: 'border-box',
  padding: '10px 14px', borderRadius: 8,
  background: '#061c36', border: `1.5px solid ${T.border}`,
  color: T.text, fontFamily: T.font, fontSize: 14, outline: 'none',
};

const genNums = () => ({ n1: Math.floor(Math.random() * 9) + 1, n2: Math.floor(Math.random() * 9) + 1 });

/* ═══════════════════════════════════════════════════════════
   ANIMATED CANVAS BACKGROUND
═══════════════════════════════════════════════════════════ */
/* ─── MOBILE RESPONSIVE STYLES ──────────────────────────── */
const MobileStyles = () => (
  <style>{`
    @media (max-width: 768px) {
      .mb-grid { grid-template-columns: 1fr !important; }
      .mb-sidebar { display: none !important; }
      .mb-sidebar.open { display: block !important; position: fixed !important; inset: 0 !important; z-index: 100 !important; background: rgba(10,37,64,0.98) !important; overflow-y: auto !important; padding: 80px 20px 20px !important; }
      .mb-nav-title { font-size: 14px !important; }
      .mb-toolbar { flex-direction: column !important; align-items: stretch !important; }
      .mb-search-wrap { width: 100% !important; }
      .mb-search-wrap input { width: 100% !important; box-sizing: border-box !important; }
      .mb-bulk-btns { flex-wrap: wrap !important; }
      .mb-sidebar-toggle { display: flex !important; }
      .mb-sidebar-close { display: block !important; }
      .mb-cards { display: block !important; }
      .mb-table { display: none !important; }
    }
    @media (min-width: 769px) {
      .mb-sidebar { display: block !important; }
      .mb-sidebar-toggle { display: none !important; }
      .mb-cards { display: none !important; }
      .mb-table { display: block !important; }
    }
    .mb-sidebar-toggle { display: none; }
  `}</style>
);

const CanvasBg = () => {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d'); let raf;
    const resize = () => { cv.width = window.innerWidth; cv.height = window.innerHeight; };
    resize(); window.addEventListener('resize', resize);
    const nodes = Array.from({ length: 50 }, () => ({
      x: Math.random() * cv.width, y: Math.random() * cv.height,
      vx: (Math.random() - .5) * .5, vy: (Math.random() - .5) * .5,
      r: Math.random() * 2 + 1, ph: Math.random() * Math.PI * 2,
    }));
    const tick = () => {
      ctx.fillStyle = 'rgba(10,37,64,0.12)';
      ctx.fillRect(0, 0, cv.width, cv.height);
      nodes.forEach(n => {
        n.x += n.vx; n.y += n.vy; n.ph += .025;
        if (n.x < 0) n.x = cv.width; if (n.x > cv.width) n.x = 0;
        if (n.y < 0) n.y = cv.height; if (n.y > cv.height) n.y = 0;
        const p = Math.sin(n.ph) * .5 + .5, s = n.r + p * 1.2;
        const g = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, s);
        g.addColorStop(0, `rgba(0,212,255,${.35 + p * .1})`);
        g.addColorStop(1, 'rgba(0,180,200,0)');
        ctx.beginPath(); ctx.arc(n.x, n.y, s, 0, Math.PI * 2);
        ctx.fillStyle = g; ctx.fill();
      });
      for (let i = 0; i < nodes.length; i++)
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x, dy = nodes[i].y - nodes[j].y, d = Math.hypot(dx, dy);
          if (d < 150) {
            ctx.beginPath(); ctx.moveTo(nodes[i].x, nodes[i].y); ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(0,212,255,${(1 - d / 150) * .1})`; ctx.lineWidth = .8; ctx.stroke();
          }
        }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(raf); };
  }, []);
  return <canvas ref={ref} style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', pointerEvents: 'none', zIndex: 0 }} />;
};

/* ═══════════════════════════════════════════════════════════
   MATH CAPTCHA
═══════════════════════════════════════════════════════════ */
const MathCaptcha = ({ onVerified, verified }) => {
  const [nums, setNums] = useState(genNums);
  const [ans, setAns] = useState('');
  const [err, setErr] = useState('');

  const verify = () => {
    if (parseInt(ans) === nums.n1 + nums.n2) { onVerified(true); setErr(''); }
    else { setErr('Wrong — try again.'); setAns(''); setNums(genNums()); onVerified(false); }
  };

  if (verified) return (
    <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(34,197,94,0.1)', border: '1px solid #22c55e', color: '#4ade80', fontFamily: T.font, fontSize: 13 }}>
      ✅ Verified
    </div>
  );

  return (
    <div style={{ padding: 14, borderRadius: 8, background: 'rgba(0,212,255,0.05)', border: `1px solid ${T.border}` }}>
      <p style={{ color: T.muted, fontFamily: T.font, fontSize: 12, marginBottom: 10 }}>Solve to confirm you're human:</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ color: T.cyan, fontFamily: T.display, fontSize: 20, fontWeight: 700, padding: '6px 14px', borderRadius: 6, background: 'rgba(0,212,255,0.08)', border: `1px solid ${T.border}` }}>
          {nums.n1} + {nums.n2} = ?
        </span>
        <input type="number" value={ans} onChange={e => setAns(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && verify()}
          placeholder="Answer" style={{ ...baseInp, width: 80, display: 'inline-block' }} />
        <button onClick={verify} type="button"
          style={{ padding: '8px 14px', borderRadius: 7, background: `linear-gradient(135deg,${T.cyan},${T.cyanDim})`, color: '#000', fontFamily: T.display, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer' }}>
          Verify
        </button>
        <button onClick={() => { setNums(genNums()); setAns(''); setErr(''); onVerified(false); }} type="button"
          style={{ padding: '8px 10px', borderRadius: 7, background: 'rgba(0,212,255,0.08)', border: `1px solid ${T.border}`, color: T.cyan, cursor: 'pointer', fontSize: 15, lineHeight: 1 }}>
          🔄
        </button>
      </div>
      {err && <p style={{ color: T.error, fontFamily: T.font, fontSize: 12, marginTop: 8 }}>{err}</p>}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   EVENT TYPE BADGE
═══════════════════════════════════════════════════════════ */
const Badge = ({ type }) => {
  const map = {
    'Webinar':       ['#0ea5e9', 'rgba(14,165,233,0.15)'],
    'Summer School': ['#a855f7', 'rgba(168,85,247,0.15)'],
    'Cyber Skill': ['#00d4ff', 'rgba(0,212,255,0.15)'],
    'Workshop':      ['#f59e0b', 'rgba(245,158,11,0.15)'],
    'Bootcamp':      ['#ef4444', 'rgba(239,68,68,0.15)'],
    'Seminar':       ['#22c55e', 'rgba(34,197,94,0.15)'],
  };
  const [c, bg] = map[type] || [T.cyan, 'rgba(0,212,255,0.1)'];
  return (
    <span style={{ padding: '2px 10px', borderRadius: 20, background: bg, color: c, fontFamily: T.font, fontSize: 11, fontWeight: 600, border: `1px solid ${c}40`, whiteSpace: 'nowrap' }}>
      {type || 'Event'}
    </span>
  );
};

/* ═══════════════════════════════════════════════════════════
   REGISTRANT DETAIL DRAWER
═══════════════════════════════════════════════════════════ */
const Drawer = ({ user, onClose, onSendEmail }) => (
  <>
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 200, backdropFilter: 'blur(4px)' }} />
    <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 28, stiffness: 260 }}
      style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 380, maxWidth: '92vw', background: T.card, borderLeft: `2px solid ${T.border}`, zIndex: 201, overflowY: 'auto', padding: 28 }}>

      <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: T.muted, fontSize: 22, cursor: 'pointer' }}>✕</button>

      <div style={{ marginTop: 8, marginBottom: 24 }}>
        <h2 style={{ color: '#fff', fontFamily: T.display, fontSize: 24, fontWeight: 700, margin: '0 0 4px' }}>
          {user.fullName || user.name || '—'}
          {user.isGroupRegistration && <span style={{ marginLeft:6, padding:'1px 7px', borderRadius:999, background:'rgba(34,197,94,0.15)', border:'1px solid #22c55e', color:'#4ade80', fontSize:10, fontWeight:700 }}>GROUP 3</span>}
        </h2>
        <p style={{ color: T.cyanDim, fontFamily: T.font, fontSize: 13, margin: 0 }}>{user.email}</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 28 }}>
        {Object.entries(user)
          .filter(([k]) => !['_id', '__v', 'createdAt', 'updatedAt'].includes(k))
          .map(([k, v]) => (
            <div key={k} style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(0,212,255,0.04)', border: `1px solid ${T.border}` }}>
              <div style={{ color: T.muted, fontFamily: T.font, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
                {k.replace(/([A-Z])/g, ' $1').trim()}
              </div>
              <div style={{ color: T.text, fontFamily: T.font, fontSize: 14, wordBreak: 'break-word' }}>
                {String(v) || '—'}
              </div>
            </div>
          ))}
      </div>

      <button onClick={() => onSendEmail([user.email])}
        style={{ width: '100%', padding: '11px 0', borderRadius: 9, border: `1.5px solid ${T.borderHi}`, background: 'rgba(0,212,255,0.08)', color: T.cyan, fontFamily: T.display, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
        📧 Send Email Campaign
      </button>
    </motion.div>
  </>
);

/* ═══════════════════════════════════════════════════════════
   EMAIL CAMPAIGN MODAL
═══════════════════════════════════════════════════════════ */
const CampaignModal = ({ recipients, onClose }) => {
  const [subj, setSubj] = useState('');
  const [body, setBody] = useState('');
  const [status, setStatus] = useState({ type: '', msg: '' });
  const [loading, setLoading] = useState(false);

  const [results, setResults] = useState(null);

  const send = async () => {
    if (!subj.trim() || !body.trim()) { setStatus({ type: 'error', msg: 'Subject and message are required.' }); return; }
    setLoading(true); setStatus({ type: '', msg: '' }); setResults(null);
    try {
      const data = await api('/api/admin/send-campaign', {
        method: 'POST',
        body: JSON.stringify({ to: recipients, subject: subj, body, from: FROM_EMAIL }),
      });
      if (data.failed && data.failed.length > 0) {
        setResults(data);
        setStatus({ type: 'warn', msg: `⚠️ Sent: ${data.sent} | Failed: ${data.failed.length}` });
      } else {
        setStatus({ type: 'success', msg: `✅ Campaign sent to ${data.sent || recipients.length} recipient(s)!` });
        setTimeout(onClose, 2200);
      }
    } catch (e) {
      setStatus({ type: 'error', msg: e.message || 'Failed to send.' });
    } finally { setLoading(false); }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300, backdropFilter: 'blur(5px)' }} />
      <motion.div initial={{ scale: .92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: .92, opacity: 0 }}
        style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '100%', maxWidth: 560, background: T.card, border: `1.5px solid ${T.border}`, borderRadius: 20, padding: 32, zIndex: 301, boxShadow: '0 30px 80px rgba(0,0,0,0.7)' }}>

        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: T.muted, fontSize: 20, cursor: 'pointer' }}>✕</button>

        <h2 style={{ color: '#fff', fontFamily: T.display, fontSize: 24, fontWeight: 700, marginBottom: 4 }}>📧 Send Email Campaign</h2>
        <p style={{ color: T.muted, fontFamily: T.font, fontSize: 12, marginBottom: 24 }}>
          From: <strong style={{ color: T.cyan }}>{FROM_EMAIL}</strong> &nbsp;·&nbsp; {recipients.length} recipient(s)
        </p>

        {status.msg && (
          <div style={{ marginBottom: 18, padding: '10px 14px', borderRadius: 8, background: status.type === 'success' ? 'rgba(34,197,94,0.1)' : status.type === 'warn' ? 'rgba(234,179,8,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${status.type === 'success' ? T.success : status.type === 'warn' ? '#eab308' : T.error}`, color: status.type === 'success' ? '#4ade80' : status.type === 'warn' ? '#fde047' : '#f87171', fontFamily: T.font, fontSize: 13 }}>
            {status.msg}
          </div>
        )}
        {results && results.failed && results.failed.length > 0 && (
          <div style={{ marginBottom: 18, padding: '12px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.3)', fontFamily: T.font, fontSize: 12 }}>
            <div style={{ color: '#f87171', fontWeight: 700, marginBottom: 6 }}>❌ Failed addresses ({results.failed.length}):</div>
            <div style={{ maxHeight: 120, overflowY: 'auto' }}>
              {results.failed.map((f, i) => (
                <div key={i} style={{ color: '#fca5a5', padding: '2px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  {f.email} — <span style={{ color: '#94a3b8' }}>{f.reason}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', color: T.muted, fontFamily: T.font, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Subject</label>
          <input value={subj} onChange={e => setSubj(e.target.value)} placeholder="Email subject…"
            style={baseInp}
            onFocus={e => { e.target.style.borderColor = T.cyan; }}
            onBlur={e => { e.target.style.borderColor = T.border; }} />
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', color: T.muted, fontFamily: T.font, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Message</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Email body…" rows={8}
            style={{ ...baseInp, resize: 'vertical' }}
            onFocus={e => { e.target.style.borderColor = T.cyan; }}
            onBlur={e => { e.target.style.borderColor = T.border; }} />
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={send} disabled={loading}
            style={{ flex: 1, padding: '12px 0', borderRadius: 10, border: 'none', background: `linear-gradient(135deg,${T.cyan},${T.cyanDim})`, color: '#000', fontFamily: T.display, fontWeight: 700, fontSize: 16, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? .6 : 1 }}>
            {loading ? 'Sending…' : `🚀 Send to ${recipients.length}`}
          </button>
          <button onClick={onClose}
            style={{ padding: '12px 20px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: 'none', color: T.muted, fontFamily: T.font, fontSize: 14, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </motion.div>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════
   CREATE EVENT MODAL
═══════════════════════════════════════════════════════════ */
const CreateEventModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', type: 'Webinar', date: '' });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const create = async () => {
    if (!form.name.trim() || !form.date) { setErr('Event name and date are required.'); return; }
    setLoading(true); setErr('');
    try {
      const ev = await api('/api/admin/events', { method: 'POST', body: JSON.stringify(form) });
      onCreated(ev); onClose();
    } catch (e) { setErr(e.message || 'Failed to create event.'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300, backdropFilter: 'blur(5px)' }} />
      <motion.div initial={{ scale: .92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '100%', maxWidth: 460, background: T.card, border: `1.5px solid ${T.border}`, borderRadius: 20, padding: 32, zIndex: 301, boxShadow: '0 30px 80px rgba(0,0,0,0.7)' }}>

        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: T.muted, fontSize: 20, cursor: 'pointer' }}>✕</button>
        <h2 style={{ color: '#fff', fontFamily: T.display, fontSize: 24, fontWeight: 700, marginBottom: 24 }}>➕ Create New Event</h2>

        {err && <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', color: '#f87171', fontFamily: T.font, fontSize: 13 }}>{err}</div>}

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', color: T.muted, fontFamily: T.font, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Event Name</label>
          <input value={form.name} onChange={set('name')} placeholder="e.g. Cyber Kill Chain Webinar"
            style={baseInp}
            onFocus={e => { e.target.style.borderColor = T.cyan; }}
            onBlur={e => { e.target.style.borderColor = T.border; }} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', color: T.muted, fontFamily: T.font, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Type</label>
          <select value={form.type} onChange={set('type')} style={{ ...baseInp, cursor: 'pointer', colorScheme: 'dark' }}>
            {['Webinar', 'Workshop', 'Bootcamp', 'Seminar'].map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 28 }}>
          <label style={{ display: 'block', color: T.muted, fontFamily: T.font, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Date</label>
          <input type="date" value={form.date} onChange={set('date')}
            style={{ ...baseInp, colorScheme: 'dark' }}
            onFocus={e => { e.target.style.borderColor = T.cyan; }}
            onBlur={e => { e.target.style.borderColor = T.border; }} />
        </div>

        <button onClick={create} disabled={loading}
          style={{ width: '100%', padding: '12px 0', borderRadius: 10, border: 'none', background: `linear-gradient(135deg,${T.cyan},${T.cyanDim})`, color: '#000', fontFamily: T.display, fontWeight: 700, fontSize: 16, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? .6 : 1 }}>
          {loading ? 'Creating…' : 'Create Event'}
        </button>
      </motion.div>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════
   LOGIN PAGE
═══════════════════════════════════════════════════════════ */
const LoginPage = ({ onLogin }) => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [captchaOk, setCaptcha] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [showPass, setShowPass] = useState(false);

  const submit = async () => {
    if (!captchaOk) { setErr('Please complete the security verification first.'); return; }
    if (!form.email || !form.password) { setErr('Email and password are required.'); return; }
    setLoading(true); setErr('');
    try {
      const data = await api('/api/admin/login', { method: 'POST', body: JSON.stringify(form) });
      sessionStorage.setItem('mbcd_admin_token', data.token);
      onLogin(data.user || { email: form.email });
    } catch (e) {
      setErr(e.message || 'Invalid credentials. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0a2540 0%, #0d3a5c 35%, #115073 65%, #0d3a5c 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative' }}>
      <CanvasBg />
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .65 }}
        style={{ width: '100%', maxWidth: 440, background: T.card, border: `1.5px solid ${T.border}`, borderRadius: 22, padding: 44, position: 'relative', zIndex: 2, boxShadow: '0 24px 70px rgba(0,0,0,0.6), 0 0 60px rgba(0,212,255,0.06)' }}>

        {/* header */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <motion.div
            animate={{ filter: ['drop-shadow(0 0 10px rgba(0,212,255,0.4))', 'drop-shadow(0 0 22px rgba(0,212,255,0.8))', 'drop-shadow(0 0 10px rgba(0,212,255,0.4))'] }}
            transition={{ duration: 2.5, repeat: Infinity }}
            style={{ margin: '0 auto 18px', width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src="/images/logo.png" alt="Martianblue" style={{ width: 80, height: 80, objectFit: 'contain', borderRadius: '50%' }} />
          </motion.div>
          <h1 style={{ color: '#fff', fontFamily: T.display, fontSize: 30, fontWeight: 700, margin: '0 0 6px' }}>Admin Portal</h1>
          <p style={{ color: T.muted, fontFamily: T.font, fontSize: 13 }}>Martianblue Cyber Defense</p>
        </div>

        {err && (
          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
            style={{ marginBottom: 22, padding: '11px 14px', borderRadius: 9, background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', color: '#f87171', fontFamily: T.font, fontSize: 13 }}>
            ⚠️ {err}
          </motion.div>
        )}

        {[
          { name: 'email',    label: 'Email Address', type: 'email',    ph: 'admin@martianbluecyberdefense.in' },
        ].map(f => (
          <div key={f.name} style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', color: T.muted, fontFamily: T.font, fontSize: 13, marginBottom: 7 }}>{f.label}</label>
            <input type={f.type} value={form[f.name]}
              onChange={e => setForm(p => ({ ...p, [f.name]: e.target.value }))}
              onFocus={e => { e.target.style.borderColor = T.cyan; e.target.style.boxShadow = `0 0 0 3px rgba(0,212,255,0.15)`; }}
              onBlur={e => { e.target.style.borderColor = T.border; e.target.style.boxShadow = 'none'; }}
              onKeyDown={e => e.key === 'Enter' && submit()}
              placeholder={f.ph} style={baseInp} />
          </div>
        ))}

        {/* Password with show/hide */}
        <div style={{ marginBottom: 26 }}>
          <label style={{ display: 'block', color: T.muted, fontFamily: T.font, fontSize: 13, marginBottom: 7 }}>Password</label>
          <div style={{ position: 'relative' }}>
            <input type={showPass ? 'text' : 'password'} value={form.password}
              onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
              onFocus={e => { e.target.style.borderColor = T.cyan; e.target.style.boxShadow = `0 0 0 3px rgba(0,212,255,0.15)`; }}
              onBlur={e => { e.target.style.borderColor = T.border; e.target.style.boxShadow = 'none'; }}
              onKeyDown={e => e.key === 'Enter' && submit()}
              placeholder="••••••••" style={{ ...baseInp, paddingRight: 44 }} />
            <button type="button" onClick={() => setShowPass(p => !p)}
              style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: T.muted, cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}
              title={showPass ? 'Hide password' : 'Show password'}>
              {showPass ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              )}
            </button>
          </div>
        </div>

        <div style={{ marginBottom: 26 }}>
          <MathCaptcha onVerified={setCaptcha} verified={captchaOk} />
        </div>

        <button onClick={submit} disabled={loading} style={{
          width: '100%', padding: '13px 0', borderRadius: 11, border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
          background: `linear-gradient(135deg,${T.cyan},${T.cyanDim})`, color: '#000',
          fontFamily: T.display, fontWeight: 700, fontSize: 18, opacity: loading ? .6 : 1,
          boxShadow: '0 10px 30px rgba(0,212,255,0.35)',
        }}>
          {loading ? 'Signing in…' : 'Sign In →'}
        </button>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   DASHBOARD
═══════════════════════════════════════════════════════════ */
const Dashboard = ({ user, onLogout }) => {
  const [events, setEvents] = useState([]);
  const [activeEvent, setActiveEvent] = useState('all');
  const [registrants, setRegistrants] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [campaignRecipients, setCampaignRecipients] = useState(null);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [typeFilter, setTypeFilter] = useState('All');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingModal, setPendingModal] = useState(false);
  const [pendingData, setPendingData] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);

  /* load events */
  useEffect(() => {
    api('/api/admin/events').then(data => setEvents(Array.isArray(data) ? data : [])).catch(console.error);
  }, []);

  /* load registrants */
  const loadRegistrants = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (search.trim()) p.set('search', search.trim());
      let data;
      if (activeEvent === 'all-audience') {
        data = await api(`/api/admin/all-audience?${p}`);
      } else if (activeEvent === 'summer-school') {
        data = await api(`/api/admin/summer-registrations?${p}`);
      } else if (activeEvent === 'cyber-skill') {
        data = await api(`/api/admin/cyberskill-registrations?${p}`);
      } else if (activeEvent === '__software__') {
        data = await api(`/api/admin/software-security-registrations?${p}`);
      } else {
        if (activeEvent !== 'all') p.set('eventId', activeEvent);
        data = await api(`/api/admin/registrations?${p}`);
      }
      setRegistrants(Array.isArray(data) ? data : (data.registrations || []));
    } catch (e) { console.error(e); setRegistrants([]); }
    finally { setLoading(false); }
  }, [activeEvent, search]);

  useEffect(() => { loadRegistrants(); }, [loadRegistrants]);

  /* derived */
  const eventTypes = ['All', ...new Set(events.map(e => e.type).filter(Boolean))];
  const visibleEvents = typeFilter === 'All' ? events : events.filter(e => e.type === typeFilter);
  const activeEventName = activeEvent === 'all-audience' ? '👥 All Audience' : activeEvent === 'summer-school' ? '☀️ Summer School' : activeEvent === '__software__' ? '🛡️ Software Security Testing Webinar' : activeEvent === 'all' ? 'All Events' : (events.find(e => e._id === activeEvent)?.name || 'Event');

  const toggleRow = id => setSelectedRows(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => selectedRows.size === registrants.length
    ? setSelectedRows(new Set())
    : setSelectedRows(new Set(registrants.map(r => r._id)));

  const selectedEmails = registrants.filter(r => selectedRows.has(r._id)).map(r => r.email).filter(Boolean);
  const allEmails = registrants.map(r => r.email).filter(Boolean);

  const fetchPendingRegistrations = async () => {
    setPendingLoading(true);
    try {
      const data = await api('/api/admin/cyberskill-pending');
      setPendingData(data.data || []);
    } catch(e) {
      setPendingData([]);
    }
    setPendingLoading(false);
  };

  const triggerPaymentReminders = async (emails = []) => {
    const isBulkAll = !emails || emails.length === 0;
    
    if (isBulkAll) {
      if (!window.confirm("Are you sure you want to email payment links to ALL unpaid Summer School registrants?")) return;
    } else {
      if (!window.confirm(`Are you sure you want to email payment links to ${emails.length} selected registrant(s)?`)) return;
    }

    setLoading(true);
    try {
      const res = await api('/api/summer-school/send-payment-reminders', { 
        method: 'POST',
        body: JSON.stringify({ emails: isBulkAll ? null : emails })
      });
      alert(res.message || 'Payment reminders sent successfully!');
    } catch (e) {
      alert('Failed to send reminders: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  /* ── render ── */
  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0a2540 0%, #0d3a5c 35%, #115073 65%, #0d3a5c 100%)', fontFamily: T.font, position: 'relative' }}>
      <MobileStyles />
      <CanvasBg />

      {/* NAV */}
      <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, height: 68, background: 'rgba(8,20,45,0.96)', backdropFilter: 'blur(24px)', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', padding: '0 32px', gap: 16 }}>
        {/* Logo + Company Name */}
        <img src="/images/logo.png" alt="Martianblue" style={{ height: 42, width: 42, objectFit: 'contain', filter: 'drop-shadow(0 0 10px rgba(0,212,255,0.6))' }} />
        <div style={{ flex: 1 }}>
          <div style={{ color: '#fff', fontFamily: T.display, fontWeight: 700, fontSize: 18, lineHeight: 1.1, letterSpacing: 0.5 }}>
            Martianblue Cyber Defense
          </div>
          <div style={{ color: T.cyan, fontFamily: T.font, fontSize: 11, fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', marginTop: 1 }}>
            Admin Panel
          </div>
        </div>
        {/* Sidebar toggle - mobile only */}
        <button className="mb-sidebar-toggle" onClick={() => setSidebarOpen(p => !p)}
          style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(0,212,255,0.1)', border: `1px solid ${T.border}`, color: T.cyan, cursor: 'pointer', fontSize: 18 }}>
          ☰
        </button>
        {/* User email */}
        <span style={{ color: T.muted, fontFamily: T.font, fontSize: 13 }}>{user.email}</span>
        {/* Sign Out - proper visible button */}
        <button onClick={onLogout}
          style={{ padding: '9px 20px', borderRadius: 9, background: 'rgba(239,68,68,0.15)', border: '1.5px solid rgba(239,68,68,0.5)', color: '#f87171', fontFamily: T.display, fontWeight: 700, fontSize: 15, cursor: 'pointer', letterSpacing: 0.5, transition: 'all .2s' }}
          onMouseEnter={e => { e.target.style.background = 'rgba(239,68,68,0.3)'; e.target.style.borderColor = '#f87171'; }}
          onMouseLeave={e => { e.target.style.background = 'rgba(239,68,68,0.15)'; e.target.style.borderColor = 'rgba(239,68,68,0.5)'; }}>
          Sign Out
        </button>
      </nav>

      {/* LAYOUT */}
      <div style={{ paddingTop: 68, position: 'relative', zIndex: 1 }}>
        <div style={{ padding: '28px 24px', display: 'grid', gridTemplateColumns: '280px 1fr', gap: 24, alignItems: 'start' }} className="mb-grid">

          {/* ══ SIDEBAR ══ */}
          <aside style={{ position: 'sticky', top: 88 }} className={`mb-sidebar${sidebarOpen ? ' open' : ''}`}>
            {/* close button mobile */}
            <button onClick={() => setSidebarOpen(false)}
              style={{ display: 'none', marginBottom: 16, background: 'none', border: 'none', color: T.muted, fontSize: 22, cursor: 'pointer' }}
              className="mb-sidebar-close">✕ Close</button>

            {/* stats */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              {[
                { icon: '📅', val: events.length, label: 'Events', color: T.cyan },
                { icon: '👥', val: registrants.length, label: 'Shown', color: '#a78bfa' },
              ].map(s => (
                <div key={s.label} style={{ background: 'rgba(13,40,71,0.8)', border: `1px solid ${T.border}`, borderRadius: 14, padding: '16px 0', textAlign: 'center', backdropFilter: 'blur(10px)' }}>
                  <div style={{ fontSize: 22, marginBottom: 6 }}>{s.icon}</div>
                  <div style={{ color: s.color, fontFamily: T.display, fontWeight: 700, fontSize: 30, lineHeight: 1 }}>{s.val}</div>
                  <div style={{ color: '#94a3b8', fontFamily: T.font, fontSize: 12, marginTop: 4, fontWeight: 500 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* create event */}
            <button onClick={() => setShowCreateEvent(true)}
              style={{ width: '100%', marginBottom: 18, padding: '10px 0', borderRadius: 11, border: `1.5px solid ${T.borderHi}`, background: 'rgba(0,212,255,0.06)', color: T.cyan, fontFamily: T.display, fontWeight: 700, fontSize: 15, cursor: 'pointer', transition: 'all .2s' }}>
              ➕ Create Event
            </button>

            {/* type pills */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
              {eventTypes.map(t => (
                <button key={t} onClick={() => setTypeFilter(t)}
                  style={{ padding: '3px 11px', borderRadius: 20, border: `1px solid ${typeFilter === t ? T.cyan : T.border}`, background: typeFilter === t ? 'rgba(0,212,255,0.12)' : 'transparent', color: typeFilter === t ? T.cyan : T.muted, fontFamily: T.font, fontSize: 11, cursor: 'pointer' }}>
                  {t}
                </button>
              ))}
            </div>

            {/* event list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 'calc(100vh - 360px)', overflowY: 'auto' }}>
              {/* ALL AUDIENCE */}
              {typeFilter === 'All' && <>
              <button onClick={() => { setActiveEvent('all-audience'); setSelectedRows(new Set()); setSidebarOpen(false); }}
                style={{ textAlign: 'left', padding: '12px 14px', borderRadius: 11, border: `1.5px solid ${activeEvent === 'all-audience' ? '#a855f7' : T.border}`, background: activeEvent === 'all-audience' ? 'rgba(168,85,247,0.1)' : T.card, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <span style={{ fontSize: 16 }}>🌍</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: activeEvent === 'all-audience' ? '#c084fc' : T.text, fontFamily: T.font, fontSize: 14, fontWeight: 600 }}>All Audience</div>
                  <div style={{ color: T.muted, fontFamily: T.font, fontSize: 11 }}>All events, no duplicates</div>
                </div>
              </button>
              {/* SUMMER SCHOOL */}
              <button onClick={() => { setActiveEvent('summer-school'); setSelectedRows(new Set()); setSidebarOpen(false); }}
                style={{ textAlign: 'left', padding: '12px 14px', borderRadius: 11, border: `1.5px solid ${activeEvent === 'summer-school' ? '#f59e0b' : T.border}`, background: activeEvent === 'summer-school' ? 'rgba(245,158,11,0.1)' : T.card, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <span style={{ fontSize: 16 }}>☀️</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: activeEvent === 'summer-school' ? '#fbbf24' : T.text, fontFamily: T.font, fontSize: 14, fontWeight: 600 }}>Online Summer School</div>
                  <div style={{ color: T.muted, fontFamily: T.font, fontSize: 11 }}>Online Summer School 2026</div>
                </div>
              </button>
              {/* CYBER SKILL PROGRAM */}
              <button onClick={() => { setActiveEvent('cyber-skill'); setSelectedRows(new Set()); setSidebarOpen(false); }}
                style={{ textAlign: 'left', padding: '12px 14px', borderRadius: 11, border: `1.5px solid ${activeEvent === 'cyber-skill' ? '#00d4ff' : T.border}`, background: activeEvent === 'cyber-skill' ? 'rgba(0,212,255,0.1)' : T.card, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <span style={{ fontSize: 16 }}>🛡️</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: activeEvent === 'cyber-skill' ? '#00d4ff' : T.text, fontFamily: T.font, fontSize: 14, fontWeight: 600 }}>Cyber Skill Program</div>
                  <div style={{ color: T.muted, fontFamily: T.font, fontSize: 11 }}>Cyber Security Skill Building 2026</div>
                </div>
              </button>

              </>}
              {visibleEvents.map(ev => (
                <button key={ev._id} onClick={() => { setActiveEvent(ev._id); setSelectedRows(new Set()); }}
                  style={{ textAlign: 'left', padding: '12px 14px', borderRadius: 11, border: `1.5px solid ${activeEvent === ev._id ? T.cyan : T.border}`, background: activeEvent === ev._id ? 'rgba(0,212,255,0.08)' : T.card, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                    <span style={{ color: activeEvent === ev._id ? T.cyan : T.text, fontFamily: T.font, fontSize: 13, fontWeight: 600, flex: 1 }}>{ev.name}</span>
                    <span style={{ color: T.cyan, fontFamily: T.display, fontWeight: 700, fontSize: 15 }}>{ev.count || 0}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <Badge type={ev.type} />
                    {ev.date && <span style={{ color: T.muted, fontFamily: T.font, fontSize: 11 }}>{new Date(ev.date).toLocaleDateString('en-GB')}</span>}
                  </div>
                </button>
              ))}
            </div>
          </aside>

          {/* ══ MAIN ══ */}
          <main style={{ minWidth: 0 }}>
            {/* toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }} className="mb-toolbar">
              <h2 style={{ color: '#f1f5f9', fontFamily: T.display, fontSize: 26, fontWeight: 700, margin: 0, flex: 1, letterSpacing: 0.5 }}>
                {activeEventName}
                <span style={{ color: '#64748b', fontFamily: T.font, fontSize: 15, fontWeight: 400, marginLeft: 10 }}>
                  ({registrants.length})
                </span>
              </h2>

              {/* search */}
              <div style={{ position: 'relative' }} className="mb-search-wrap">
                <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontSize: 14, pointerEvents: 'none' }}>🔍</span>
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search by email / phone / college…"
                  style={{ ...baseInp, paddingLeft: 38, paddingRight: search ? 38 : 16, width: 310, fontSize: 13 }}
                  onFocus={e => { e.target.style.borderColor = T.cyan; e.target.style.boxShadow = '0 0 0 3px rgba(0,212,255,0.15)'; }}
                  onBlur={e => { e.target.style.borderColor = T.border; e.target.style.boxShadow = 'none'; }} />
                {search && (
                  <button onClick={() => setSearch('')}
                    style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', fontSize: 16, cursor: 'pointer', padding: 2 }}>✕</button>
                )}
              </div>

              {/* bulk campaign */}
              {selectedRows.size > 0 && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button onClick={() => { const valid = selectedEmails.filter(e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)); if(!valid.length){alert('No valid email addresses selected.');return;} setCampaignRecipients(valid); }}
                    style={{ padding: '9px 18px', borderRadius: 9, border: `1.5px solid rgba(0,212,255,0.5)`, background: 'rgba(0,212,255,0.12)', color: '#67e8f9', fontFamily: T.display, fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    📧 Send to {selectedRows.size}
                  </button>

                  {/* send payment reminders (Summer School only) */}
                  {activeEvent === 'summer-school' && (
                    <button onClick={() => triggerPaymentReminders(selectedEmails)}
                      style={{ padding: '9px 18px', borderRadius: 9, border: '1.5px solid rgba(34,197,94,0.5)', background: 'rgba(34,197,94,0.1)', color: '#4ade80', fontFamily: T.display, fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      💸 Send Payment Links
                    </button>
                  )}

                  <button onClick={() => setSelectedRows(new Set())}
                    style={{ padding: '9px 13px', borderRadius: 9, border: `1.5px solid rgba(239,68,68,0.4)`, background: 'rgba(239,68,68,0.08)', color: '#f87171', fontFamily: T.font, fontSize: 13, cursor: 'pointer' }}>
                    ✕ Clear
                  </button>
                </div>
              )}

              {/* send all */}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { const valid = allEmails.filter(e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)); if(!valid.length){alert('No valid email addresses found.');return;} setCampaignRecipients(valid); }}
                  style={{ padding: '9px 18px', borderRadius: 9, border: '1.5px solid rgba(168,85,247,0.5)', background: 'rgba(168,85,247,0.1)', color: '#c084fc', fontFamily: T.display, fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  📨 All ({allEmails.length})
                </button>

                {/* bulk send payment reminders (Summer School only) */}
                {activeEvent === 'summer-school' && (
                  <button onClick={() => triggerPaymentReminders([])}
                    style={{ padding: '9px 18px', borderRadius: 9, border: '1.5px solid rgba(34,197,94,0.5)', background: 'rgba(34,197,94,0.1)', color: '#4ade80', fontFamily: T.display, fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    💸 Send to ALL Unpaid
                  </button>
                )}
                {/* Pending/Failed registrations (Cyber Skill only) */}
                {activeEvent === 'cyber-skill' && (
                  <button onClick={() => { setPendingModal(true); fetchPendingRegistrations(); }}
                    style={{ padding: '9px 18px', borderRadius: 9, border: '1.5px solid rgba(251,191,36,0.5)', background: 'rgba(251,191,36,0.1)', color: '#fbbf24', fontFamily: T.display, fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    ⏳ Pending / Failed
                  </button>
                )}
              </div>
            </div>

            {/* table / cards */}
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>
              {loading ? (
                <div style={{ padding: 70, textAlign: 'center', color: T.muted }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
                  Loading registrants…
                </div>
              ) : registrants.length === 0 ? (
                <div style={{ padding: 70, textAlign: 'center', color: T.muted }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
                  No registrants found.
                </div>
              ) : (
                <>
                {/* ── MOBILE CARDS (hidden on desktop) ── */}
                <div className="mb-cards" style={{ display: 'none' }}>
                  <div style={{ padding: '12px 16px', background: 'rgba(0,0,0,0.45)', borderBottom: `2px solid rgba(0,212,255,0.25)`, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="checkbox" checked={selectedRows.size === registrants.length}
                      onChange={toggleAll} style={{ accentColor: T.cyan, cursor: 'pointer', width: 15, height: 15 }} />
                    <span style={{ color: '#cbd5e1', fontFamily: T.font, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                      Select All ({registrants.length})
                    </span>
                  </div>
                  {registrants.map((r, i) => (
                    <motion.div key={r._id || i}
                      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.018, 0.3) }}
                      style={{ padding: '14px 16px', borderBottom: `1px solid rgba(0,212,255,0.07)`, background: selectedRows.has(r._id) ? 'rgba(0,212,255,0.06)' : 'transparent' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        <input type="checkbox" checked={selectedRows.has(r._id)}
                          onChange={() => toggleRow(r._id)} style={{ accentColor: T.cyan, cursor: 'pointer', width: 15, height: 15, flexShrink: 0 }} />
                        <span onClick={() => setSelectedUser(r)} style={{ color: '#f1f5f9', fontFamily: T.font, fontSize: 15, fontWeight: 700, cursor: 'pointer', flex: 1 }}>
                          {r.fullName || r.name || '—'}
                          {r.isGroupRegistration && <span style={{ marginLeft:6, padding:'1px 7px', borderRadius:999, background:'rgba(34,197,94,0.15)', border:'1px solid #22c55e', color:'#4ade80', fontSize:10, fontWeight:700 }}>GROUP 3</span>}
                        </span>
                        <Badge type={r.eventType || 'Webinar'} />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 25, marginBottom: 10 }}>
                        <span style={{ color: '#67e8f9', fontFamily: T.font, fontSize: 13 }}>{r.email || '—'}</span>
                        <span style={{ color: '#e2e8f0', fontFamily: T.font, fontSize: 13 }}>{r.mobile || r.phone || '—'}</span>
                        <span style={{ color: '#94a3b8', fontFamily: T.font, fontSize: 12 }}>{r.collegeName || r.college || '—'}</span>
                        {activeEvent === 'summer-school' && (
                          <>
                            <span style={{ color: '#94a3b8', fontFamily: T.font, fontSize: 12 }}>Payment: <strong style={{color: r.paymentStatus === 'PAID' ? T.success : r.paymentStatus === 'PENDING' ? '#fbbf24' : 'inherit'}}>{r.paymentStatus || '—'}</strong></span>
                            <span style={{ color: '#94a3b8', fontFamily: T.font, fontSize: 12 }}>Order: {r.razorpayOrderId || '—'}</span>
                          </>
                        )}
                        <span style={{ color: '#94a3b8', fontFamily: T.font, fontSize: 12 }}>
                          {r.registeredAt ? new Date(r.registeredAt).toLocaleDateString('en-GB') : '—'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 8, paddingLeft: 25 }}>
                        <button onClick={() => setSelectedUser(r)}
                          style={{ flex: 1, padding: '8px 0', borderRadius: 7, border: `1.5px solid rgba(0,212,255,0.4)`, background: 'rgba(0,212,255,0.1)', color: '#67e8f9', fontFamily: T.font, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                          Details
                        </button>
                        <button onClick={() => setCampaignRecipients([r.email])}
                          style={{ padding: '8px 14px', borderRadius: 7, border: '1.5px solid rgba(168,85,247,0.4)', background: 'rgba(168,85,247,0.1)', color: '#c084fc', fontFamily: T.font, fontSize: 13, cursor: 'pointer' }}>
                          📧
                        </button>
                        {activeEvent === 'summer-school' && r.paymentStatus !== 'PAID' && (
                          <button onClick={() => triggerPaymentReminders([r.email])}
                            style={{ padding: '8px 14px', borderRadius: 7, border: '1.5px solid rgba(34,197,94,0.4)', background: 'rgba(34,197,94,0.1)', color: '#4ade80', fontFamily: T.font, fontSize: 13, cursor: 'pointer' }}>
                            💸
                          </button>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* ── DESKTOP TABLE (hidden on mobile) ── */}
                <div className="mb-table" style={{ overflowX: 'auto', width: '100%', paddingBottom: '10px' }}>
                  <table style={{ minWidth: 'max-content', width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'rgba(0,0,0,0.45)', borderBottom: `2px solid rgba(0,212,255,0.25)` }}>
                        <th style={{ padding: '14px 16px', width: 40 }}>
                          <input type="checkbox" checked={selectedRows.size === registrants.length}
                            onChange={toggleAll} style={{ accentColor: T.cyan, cursor: 'pointer', width: 15, height: 15 }} />
                        </th>
                        {[
                          'Name', 'Email', 'Phone', 'College / Company', 'Status', 'Event Type', 'Registered',
                          ...(activeEvent === 'summer-school' ? ['Payment', 'Order ID'] : []),
                          'Actions'
                        ].map(h => (
                          <th key={h} style={{ padding: '14px 16px', textAlign: 'left', color: '#cbd5e1', fontFamily: T.font, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {registrants.map((r, i) => (
                        <motion.tr key={r._id || i}
                          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.018, 0.4) }}
                          style={{ borderBottom: `1px solid rgba(0,212,255,0.07)`, cursor: 'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.06)'}
                          onMouseLeave={e => e.currentTarget.style.background = selectedRows.has(r._id) ? 'rgba(0,212,255,0.06)' : 'transparent'}>
                          <td style={{ padding: '13px 16px' }}>
                            <input type="checkbox" checked={selectedRows.has(r._id)}
                              onChange={() => toggleRow(r._id)} style={{ accentColor: T.cyan, cursor: 'pointer', width: 15, height: 15 }} />
                          </td>
                          <td onClick={() => setSelectedUser(r)} style={{ padding: '13px 16px', color: '#f1f5f9', fontFamily: T.font, fontSize: 13, whiteSpace: 'nowrap', fontWeight: 600 }}>
                            {r.fullName || r.name || '—'}
                          {r.isGroupRegistration && <span style={{ marginLeft:6, padding:'1px 7px', borderRadius:999, background:'rgba(34,197,94,0.15)', border:'1px solid #22c55e', color:'#4ade80', fontSize:10, fontWeight:700 }}>GROUP 3</span>}
                            {r.isGroupRegistration && <span style={{ marginLeft:6, padding:'1px 7px', borderRadius:999, background:'rgba(34,197,94,0.15)', border:'1px solid #22c55e', color:'#4ade80', fontSize:10, fontWeight:700 }}>GROUP 3</span>}
                          </td>
                          <td style={{ padding: '13px 16px', color: '#67e8f9', fontFamily: T.font, fontSize: 13 }}>{r.email || '—'}</td>
                          <td style={{ padding: '13px 16px', color: '#e2e8f0', fontFamily: T.font, fontSize: 13, whiteSpace: 'nowrap' }}>{r.mobile || r.phone || '—'}</td>
                          <td style={{ padding: '13px 16px', color: '#e2e8f0', fontFamily: T.font, fontSize: 13 }}>{r.collegeName || r.college || '—'}</td>
                          <td style={{ padding: '13px 16px', color: '#e2e8f0', fontFamily: T.font, fontSize: 13 }}>{r.currentStatus || '—'}</td>
                          <td style={{ padding: '13px 16px' }}>
                            <Badge type={r.eventType || (events.find(e => e._id === r.eventId)?.type) || 'Webinar'} />
                          </td>
                          <td style={{ padding: '13px 16px', color: '#94a3b8', fontFamily: T.font, fontSize: 12, whiteSpace: 'nowrap' }}>
                            {r.registeredAt ? new Date(r.registeredAt).toLocaleDateString('en-GB') : (r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-GB') : '—')}
                          </td>
                          {activeEvent === 'summer-school' && (
                            <>
                              <td style={{ padding: '13px 16px', color: r.paymentStatus === 'PAID' ? T.success : r.paymentStatus === 'PENDING' ? '#fbbf24' : '#e2e8f0', fontFamily: T.font, fontSize: 13, whiteSpace: 'nowrap', fontWeight: 600 }}>{r.paymentStatus || '—'}</td>
                              <td style={{ padding: '13px 16px', color: '#94a3b8', fontFamily: T.font, fontSize: 12, whiteSpace: 'nowrap' }}>{r.razorpayOrderId || '—'}</td>
                            </>
                          )}
                          <td style={{ padding: '13px 16px' }}>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button onClick={() => setSelectedUser(r)}
                                style={{ padding: '6px 14px', borderRadius: 7, border: `1.5px solid rgba(0,212,255,0.4)`, background: 'rgba(0,212,255,0.1)', color: '#67e8f9', fontFamily: T.font, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                Details
                              </button>
                              <button onClick={() => setCampaignRecipients([r.email])}
                                style={{ padding: '6px 12px', borderRadius: 7, border: '1.5px solid rgba(168,85,247,0.4)', background: 'rgba(168,85,247,0.1)', color: '#c084fc', fontFamily: T.font, fontSize: 12, cursor: 'pointer' }}>
                                📧
                              </button>
                              {activeEvent === 'summer-school' && r.paymentStatus !== 'PAID' && (
                                <button onClick={() => triggerPaymentReminders([r.email])}
                                  style={{ padding: '6px 12px', borderRadius: 7, border: '1.5px solid rgba(34,197,94,0.4)', background: 'rgba(34,197,94,0.1)', color: '#4ade80', fontFamily: T.font, fontSize: 12, cursor: 'pointer' }}>
                                  💸
                                </button>
                              )}
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* MODALS */}
      <AnimatePresence>
        {selectedUser && (
          <Drawer user={selectedUser} onClose={() => setSelectedUser(null)}
            onSendEmail={emails => { setSelectedUser(null); setCampaignRecipients(emails); }} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {campaignRecipients && (
          <CampaignModal recipients={campaignRecipients} onClose={() => setCampaignRecipients(null)} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showCreateEvent && (
          <CreateEventModal onClose={() => setShowCreateEvent(false)}
            onCreated={ev => { setEvents(p => [...p, ev]); }} />
        )}
      </AnimatePresence>
      {pendingModal && (
        <div style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'#0a1e3d', border:'1.5px solid rgba(251,191,36,0.4)', borderRadius:16, width:'100%', maxWidth:900, maxHeight:'85vh', overflow:'hidden', display:'flex', flexDirection:'column' }}>
            <div style={{ padding:'18px 24px', borderBottom:'1px solid rgba(251,191,36,0.2)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <h3 style={{ color:'#fbbf24', fontFamily:T.display, fontSize:22, fontWeight:700, margin:0 }}>⏳ Pending / Failed Registrations</h3>
                <p style={{ color:T.muted, fontFamily:T.font, fontSize:12, margin:'4px 0 0' }}>In-memory sessions — form submitted but payment not completed</p>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={fetchPendingRegistrations} style={{ padding:'7px 14px', borderRadius:8, border:'1.5px solid rgba(0,212,255,0.4)', background:'rgba(0,212,255,0.1)', color:'#67e8f9', fontFamily:T.font, fontSize:13, cursor:'pointer' }}>🔄 Refresh</button>
                <button onClick={() => setPendingModal(false)} style={{ padding:'7px 14px', borderRadius:8, border:'1.5px solid rgba(239,68,68,0.4)', background:'rgba(239,68,68,0.1)', color:'#f87171', fontFamily:T.font, fontSize:13, cursor:'pointer' }}>✕ Close</button>
              </div>
            </div>
            <div style={{ overflowY:'auto', padding:16 }}>
              {pendingLoading ? (
                <div style={{ textAlign:'center', padding:60, color:T.muted }}>⏳ Loading...</div>
              ) : pendingData.length === 0 ? (
                <div style={{ textAlign:'center', padding:60, color:T.muted }}>
                  <div style={{ fontSize:40, marginBottom:12 }}>✅</div>
                  No pending or failed registrations
                </div>
              ) : (
                <div style={{ overflowX:'auto' }}>
                  <div style={{ color:T.muted, fontFamily:T.font, fontSize:12, marginBottom:12 }}>{pendingData.length} pending session{pendingData.length !== 1 ? 's' : ''} in memory</div>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontFamily:T.font, fontSize:13 }}>
                    <thead>
                      <tr style={{ background:'rgba(251,191,36,0.08)' }}>
                        {['Name','Email','Mobile','Status','College','Duration','Amount','Age','Order ID'].map(h => (
                          <th key={h} style={{ padding:'10px 12px', textAlign:'left', color:'#fbbf24', fontWeight:700, borderBottom:'1px solid rgba(251,191,36,0.2)', whiteSpace:'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pendingData.map((r, i) => (
                        <tr key={r.sessionId} style={{ background: i%2===0 ? 'rgba(255,255,255,0.02)' : 'transparent', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                          <td style={{ padding:'10px 12px', color:T.text }}>
                            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                              {r.isGroup
                                ? <span style={{ color:T.text, fontWeight:600 }}>👥 {r.fullName}</span>
                                : r.fullName}
                              {r.isGroup && <span style={{ padding:'1px 7px', borderRadius:999, background:'rgba(34,197,94,0.15)', border:'1px solid #22c55e', color:'#4ade80', fontSize:10, fontWeight:700 }}>GROUP 3</span>}
                              {r.source === 'db' && <span style={{ padding:'1px 7px', borderRadius:999, background:'rgba(99,102,241,0.15)', border:'1px solid #6366f1', color:'#a5b4fc', fontSize:10, fontWeight:700 }}>DB</span>}
                            </div>
                            {r.isGroup && r.members && (
                              <div style={{ marginTop:6, paddingLeft:8, borderLeft:'2px solid #22c55e' }}>
                                {r.members.map((m, i) => (
                                  <div key={i} style={{ color:'#94a3b8', fontSize:11, fontFamily:T.font, marginBottom:2 }}>
                                    {i+1}. {m.fullName} — {m.email} — {m.mobile}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                          <td style={{ padding:'10px 12px', color:'#67e8f9' }}>{r.email}</td>
                          <td style={{ padding:'10px 12px', color:T.text }}>{r.mobile}</td>
                          <td style={{ padding:'10px 12px' }}><span style={{ padding:'2px 8px', borderRadius:999, background:'rgba(251,191,36,0.15)', color:'#fbbf24', fontSize:11, fontWeight:600 }}>{r.currentStatus}</span></td>
                          <td style={{ padding:'10px 12px', color:T.muted, maxWidth:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.collegeName}</td>
                          <td style={{ padding:'10px 12px', color:T.text }}>{r.preferredDuration}</td>
                          <td style={{ padding:'10px 12px', color:'#4ade80', fontWeight:600 }}>₹{((r.amount||0)/100).toLocaleString()}</td>
                          <td style={{ padding:'10px 12px', color: r.ageMinutes > 30 ? '#f87171' : '#fbbf24' }}>{r.ageMinutes}m ago</td>
                          <td style={{ padding:'10px 12px', color:T.muted, fontSize:11 }}>{r.razorpayOrderId || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   APP ROOT — handles auth state
═══════════════════════════════════════════════════════════ */
export default function CyberEduAdmin() {
  const [user, setUser] = useState(() => {
    // Persist session across page reloads (token stored in sessionStorage)
    const token = sessionStorage.getItem('mbcd_admin_token');
    return token ? { email: 'admin' } : null;
  });

  const logout = () => {
    sessionStorage.removeItem('mbcd_admin_token');
    setUser(null);
  };

  return user ? <Dashboard user={user} onLogout={logout} /> : <LoginPage onLogin={setUser} />;
}